<template>
  <div>
    <section class="section is-main-section">
      <CardComponent>
        <div class="section">
          <div class="has-text-centered">
            <h1 class="title">Manage Metadata</h1>
          </div>
          <div class="columns">
            <div class="column">
              <b-field label="Select Metadata Type" class="has-margin-top-30">
                <b-select v-model="metadataType" @input="changeMetadata">
                  <option value="actor">Actors</option>
                  <option value="director">Directors</option>
                  <option value="genre">Genres</option>
                  <option value="country">Countries</option>
                  <option value="type">Content Type</option>
                  <option value="right">Distribution Rights</option>
                  <option value="distribution">Distribution Methods</option>
                  <option value="tag">Tags</option>
                  <option value="language">Languages</option>
                  <option value="resolution">Resolutions</option>
                  <option value="audio">Audio</option>
                  <option value="supplier">Suppliers</option>
                </b-select>
              </b-field>
            </div>
            <div class="column">
              <div
                v-if="metadataType"
                class="has-text-right"
                style="margin-top: 60px"
              >
                <b-button
                  type="is-primary"
                  icon-left="plus"
                  @click="addMetadata"
                  >Add</b-button
                >
              </div>
            </div>
          </div>

          <div v-if="metadataType">
            <b-field class="has-margin-top-15">
              <b-input
                v-model="search"
                placeholder="Search..."
                type="search"
                icon="magnify"
              >
              </b-input>
            </b-field>
            <b-table
              :data="filteredMetadata"
              :bordered="false"
              :striped="true"
              :narrowed="true"
              :hoverable="true"
              :loading="loading"
              :focusable="false"
              :mobile-cards="true"
              :paginated="true"
            >
              <template slot-scope="props">
                <b-table-column field="value" label="Value">
                  {{ props.row.value }}
                </b-table-column>

                <b-table-column field="display_order" label="Display Order">
                  {{ props.row.display_order }}
                </b-table-column>

                <b-table-column>
                  <b-dropdown aria-role="list">
                    <button slot="trigger" class="button is-primary">
                      <span>Actions</span>
                      <b-icon icon="menu-down"></b-icon>
                    </button>

                    <b-dropdown-item aria-role="listitem" :has-link="true"
                      ><a href="" @click.prevent="editMetadata(props.row)"
                        >Edit</a
                      ></b-dropdown-item
                    >
                    <b-dropdown-item aria-role="listitem" :has-link="true"
                      ><a href="" @click.prevent="viewLink(props.row)"
                        >Manage Links</a
                      ></b-dropdown-item
                    >
                    <b-dropdown-item
                      v-if="props.row.media_type === 'series'"
                      aria-role="listitem"
                      :has-link="true"
                      >Link</b-dropdown-item
                    >
                    <b-dropdown-item
                      aria-role="listitem"
                      @click="removeMetadata(props.row)"
                      >Delete</b-dropdown-item
                    >
                  </b-dropdown>
                </b-table-column>
              </template>

              <template slot="empty">
                <section class="section">
                  <div class="content has-text-grey has-text-centered">
                    <p>
                      <b-icon icon="emoticon-sad" size="is-medium"> </b-icon>
                    </p>
                    <p>No metadata.</p>
                  </div>
                </section>
              </template>
            </b-table>
          </div>
        </div>
      </CardComponent>
    </section>
    <b-modal
      :active.sync="editMetadataModal"
      trap-focus
      aria-role="dialog"
      aria-modal
      has-modal-card
      @close="editMetadataModal = false"
    >
      <div class="modal-card" style="width: auto">
        <header class="modal-card-head">
          <p class="modal-card-title">
            {{ addingMetadata ? 'Add' : 'Edit' }} Metadata
          </p>
        </header>
        <ValidationObserver ref="observer" v-slot="{ passes }">
          <section class="modal-card-body">
            <form>
              <div class="columns">
                <div class="column">
                  <div class="control">
                    <ValidationProvider
                      v-slot="{ errors }"
                      name="Value"
                      rules="required"
                    >
                      <b-field
                        label="Value"
                        :type="{ 'is-danger': errors.length > 0 }"
                        :message="errors[0]"
                      >
                        <b-input v-model="item.value" />
                      </b-field>
                    </ValidationProvider>
                  </div>
                </div>
                <div class="column">
                  <div class="control">
                    <ValidationProvider
                      v-slot="{ errors }"
                      name="Display Order"
                      rules="required"
                    >
                      <b-field
                        label="Display Order"
                        :type="{ 'is-danger': errors.length > 0 }"
                        :message="errors[0]"
                      >
                        <b-input v-model="item.display_order" type="number">
                        </b-input>
                      </b-field>
                    </ValidationProvider>
                  </div>
                </div>
              </div>
            </form>
          </section>
          <footer class="modal-card-foot" style="justify-content: flex-end">
            <b-button @click="editMetadataModal = false"> Cancel </b-button>
            <b-button type="is-primary" @click="passes(saveMetadata)">
              Save
            </b-button>
          </footer>
        </ValidationObserver>
      </div>
    </b-modal>
    <b-modal
      :active.sync="editLinkModal"
      trap-focus
      aria-role="dialog"
      aria-modal
      has-modal-card
      width="700px"
      @close="editLinkModal = false"
    >
      <div class="modal-card">
        <header class="modal-card-head">
          <p class="modal-card-title">Edit Metadata Links - {{ link.value }}</p>
        </header>
        <ValidationObserver ref="observer" v-slot="{ passes }">
          <section class="modal-card-body">
            <p v-if="links.length === 0">No items linked to this item.</p>
            <div v-for="link in links" :key="link.id">
              {{ link.value }}
              <a href="" @click.prevent="removeLink(link)"
                ><b-icon icon="close"
              /></a>
            </div>
            <h3 class="subtitle has-margin-top-30">Add Link</h3>
            <div class="columns">
              <div class="column">
                <b-field label="Select Metadata Type">
                  <b-select
                    v-model="linkMetadataType"
                    @input="changeLinkMetadata"
                  >
                    <option value="actor">Actors</option>
                    <option value="genre">Genres</option>
                    <option value="country">Countries</option>
                    <option value="excludedcountry">Excluded Countries</option>
                    <option value="type">Content Type</option>
                    <option value="right">Distribution Rights</option>
                    <option value="distribution">Distribution Methods</option>
                    <option value="tag">Tags</option>
                    <option value="language">Languages</option>
                  </b-select>
                </b-field>
              </div>
              <div class="column">
                <b-field v-if="linkMetadataType" label="Metadata">
                  <b-autocomplete
                    :keep-first="true"
                    :open-on-focus="true"
                    :data="linkItems"
                    field="value"
                    @select="option => (selectedLink = option.id)"
                  />
                </b-field>
              </div>
            </div>
            <b-button v-if="selectedLink" type="is-primary" @click="addLink">
              Add
            </b-button>
          </section>
          <footer class="modal-card-foot" style="justify-content: flex-end">
            <b-button @click="editLinkModal = false"> Close </b-button>
          </footer>
        </ValidationObserver>
      </div>
    </b-modal>
  </div>
</template>

<script>
import CardComponent from '@/components/CardComponent'
import MetadataApi from '@/api/metadata'
export default {
  name: 'ManageMetadata',
  components: {
    CardComponent
  },
  metaInfo: {
    title: 'Manage Metadata'
  },
  data() {
    return {
      loading: true,
      metadataType: '',
      items: [],
      search: '',
      item: {},
      addingMetadata: false,
      editMetadataModal: false,
      editLinkModal: false,
      link: {},
      links: {},
      linkMetadataType: '',
      linkItems: [],
      selectedLink: null
    }
  },
  computed: {
    filteredMetadata() {
      if (this.search !== '' && this.items.length > 0) {
        let query = this.search.toLowerCase()
        return this.items.filter(item =>
          item.value.toLowerCase().includes(query)
        )
      } else {
        return this.items
      }
    }
  },
  methods: {
    async changeMetadata() {
      this.loading = true
      this.items = await MetadataApi.getAllByType(this.metadataType)
      this.loading = false
    },
    addMetadata() {
      this.item = {
        display_order: 0,
        metadata_type: this.metadataType
      }
      this.addingMetadata = true
      this.editMetadataModal = true
    },
    editMetadata(entry) {
      this.item = entry
      this.addingMetadata = false
      this.editMetadataModal = true
    },
    removeMetadata(entry) {
      this.$buefy.dialog.confirm({
        message: 'Are you sure you want to delete this metadata entry?',
        onConfirm: async () => {
          try {
            await MetadataApi.remove(entry)
            this.$buefy.toast.open({
              message: 'Metadata entry deleted',
              position: 'is-top',
              type: 'is-success'
            })
            this.changeMetadata()
          } catch (e) {
            let message = 'Error delete metadata entry'
            if (e.data && e.data.err && e.data.err === 'E_IN_USE') {
              message =
                'We can not delete this metadata entry as it is linked to other media'
            }
            this.$buefy.toast.open({
              message: message,
              position: 'is-top',
              type: 'is-danger'
            })
          }
        }
      })
    },
    async saveMetadata() {
      try {
        let message = 'Metadata item updated successfully'
        if (this.item.id) {
          await MetadataApi.update(this.item)
        } else {
          message = 'Metadata item added successfully'
          await MetadataApi.add(this.item)
        }
        this.$buefy.toast.open({
          message: message,
          position: 'is-top',
          type: 'is-success'
        })
        this.changeMetadata()
        this.editMetadataModal = false
      } catch (e) {
        let message = 'Error saving metadata change'
        if (e.data && e.data.err && e.data.err === 'E_ALREADY_EXISTS') {
          message = 'This metadata value already exists'
        }
        this.$buefy.toast.open({
          message: message,
          position: 'is-top',
          type: 'is-danger'
        })
      }
    },
    async viewLink(item) {
      this.link = item
      this.links = await MetadataApi.getLinks(item.id)
      this.editLinkModal = true
    },
    async changeLinkMetadata() {
      this.linkItems = await MetadataApi.getAllByType(this.linkMetadataType)
    },
    async removeLink(item) {
      await MetadataApi.removeLink(item)
      this.links = await MetadataApi.getLinks(this.link.id)
    },
    async addLink() {
      await MetadataApi.addLink({
        metadata_id: this.link.id,
        linked_metadata_id: this.selectedLink
      })
      this.links = await MetadataApi.getLinks(this.link.id)
    }
  }
}
</script>
